import { ComponentType, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import { Link } from '@backstage/core-components';
import { TemplateCardComponentProps } from '@backstage/plugin-scaffolder-react/alpha';
import CodeIcon from '@material-ui/icons/Code';
import ExtensionIcon from '@material-ui/icons/ExtensionOutlined';
import LibraryIcon from '@material-ui/icons/LibraryBooksOutlined';
import StorageIcon from '@material-ui/icons/Storage';
import WebIcon from '@material-ui/icons/Web';
import { EntityDisplayLink } from '../shared/EntityDisplayLink';
import { toSafeHref } from '../shared/links';
import { useByxT } from '../shared/translations';
import { spaceGrotesk } from '../theme/fonts';
import { byxKindChips, byxScheme } from '../theme/tokens';

const kindChips = (type: string) =>
  byxKindChips[type === 'dark' ? 'dark' : 'light'];

const useStyles = makeStyles(theme => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    height: '100%',
    boxSizing: 'border-box',
    padding: theme.spacing(2.25),
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 14,
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
  },
  tile: {
    flex: 'none',
    display: 'grid',
    placeItems: 'center',
    width: 36,
    height: 36,
    borderRadius: 10,
    '& svg': { fontSize: 18 },
  },
  // Icon tile tinted like the catalog's kind chips.
  tileComponent: {
    background: kindChips(theme.palette.type).component.bg,
    color: kindChips(theme.palette.type).component.ink,
  },
  tileResource: {
    background: kindChips(theme.palette.type).resource.bg,
    color: kindChips(theme.palette.type).resource.ink,
  },
  tileApi: {
    background: kindChips(theme.palette.type).api.bg,
    color: kindChips(theme.palette.type).api.ink,
  },
  heading: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
  },
  title: {
    margin: 0,
    fontFamily: spaceGrotesk,
    fontSize: 16,
    fontWeight: 600,
    lineHeight: 1.3,
    color: theme.palette.text.primary,
  },
  badge: {
    padding: '1px 8px',
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 700,
    background: kindChips(theme.palette.type).resource.bg,
    color: kindChips(theme.palette.type).resource.ink,
  },
  desc: {
    flex: 1,
    margin: 0,
    fontSize: 14,
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
  },
  tags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  tag: {
    padding: '2px 8px',
    borderRadius: 6,
    fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
    fontSize: 11.5,
    background: byxScheme(theme.palette.type).chip,
    color: byxScheme(theme.palette.type).chipInk,
  },
  links: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.75),
    fontSize: 13,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    paddingTop: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  owner: {
    marginRight: 'auto',
    fontSize: 12,
    color: theme.palette.text.secondary,
    // The owner links to its page but keeps the card's quiet footer tone.
    '& a': { color: 'inherit' },
  },
  stepsButton: {
    height: 36,
    padding: theme.spacing(0, 1.25),
    border: 0,
    borderRadius: 10,
    background: 'none',
    color: theme.palette.text.secondary,
    font: 'inherit',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.text.primary,
      background: theme.palette.action.hover,
    },
  },
  useButton: {
    height: 36,
    padding: theme.spacing(0, 2),
    border: 0,
    borderRadius: 10,
    // A filled button with white ink: primary-fill (D-24).
    background: byxScheme(theme.palette.type).accentFill,
    color: byxScheme(theme.palette.type).accentInk,
    font: 'inherit',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    '&:hover': { filter: 'brightness(1.12)' },
  },
  steps: {
    margin: 0,
    padding: 0,
    listStyle: 'none',
    fontSize: 12.5,
    color: theme.palette.text.secondary,
  },
  step: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1.25),
    '& + &': { marginTop: 7 },
  },
  stepNumber: {
    fontFamily: spaceGrotesk,
    fontSize: 11,
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
}));

/** The golden-path tag becomes the card's badge instead of a chip. */
const GOLDEN_PATH_TAG = 'golden-path';

type Kind = 'component' | 'resource' | 'api';

function kindOf(type?: string): Kind {
  if (!type) return 'component';
  if (type === 'resource') return 'resource';
  if (type === 'service' || type === 'website' || type === 'library') {
    return 'component';
  }
  return 'api';
}

const TYPE_ICONS: Record<string, ComponentType> = {
  service: CodeIcon,
  website: WebIcon,
  library: LibraryIcon,
  resource: StorageIcon,
};

const TILE_CLASSES = {
  component: 'tileComponent',
  resource: 'tileResource',
  api: 'tileApi',
} as const;

/**
 * Template card from the "Backstage BYX" v2 design: type icon, title with
 * the golden-path badge, description, tags and a footer with the owner,
 * the steps toggle and "Usar". Replaces the default TemplateCard via
 * SwappableComponentBlueprint; the list, wizard and task pages stay the
 * plugin's.
 */
export function ByxTemplateCard(props: Readonly<TemplateCardComponentProps>) {
  const { template, additionalLinks, onSelected } = props;
  const classes = useStyles();
  const { t } = useByxT();
  const [expanded, setExpanded] = useState(false);

  const tileClass = classes[TILE_CLASSES[kindOf(template.spec.type)]];
  const Icon = TYPE_ICONS[template.spec.type] ?? ExtensionIcon;
  const allTags = template.metadata.tags ?? [];
  const isGoldenPath = allTags.includes(GOLDEN_PATH_TAG);
  const tags = allTags.filter(tag => tag !== GOLDEN_PATH_TAG);
  const steps = (template.spec.steps ?? []).map(
    step => step.name ?? step.action,
  );
  const title = template.metadata.title ?? template.metadata.name;
  // Template links come from entity data: only safe targets render (SR-01).
  const links = (additionalLinks ?? []).flatMap(link => {
    const to = toSafeHref(link.url);
    return to ? [{ to, text: link.text }] : [];
  });

  return (
    <article className={classes.card} aria-label={title}>
      <div className={classes.header}>
        <span aria-hidden="true" className={`${classes.tile} ${tileClass}`}>
          <Icon />
        </span>
        <div className={classes.heading}>
          <h3 className={classes.title}>{title}</h3>
          {isGoldenPath && (
            <span className={classes.badge}>
              {t('templateCard.goldenPath')}
            </span>
          )}
        </div>
      </div>
      <p className={classes.desc}>{template.metadata.description}</p>
      {tags.length > 0 && (
        <ul className={classes.tags} aria-label={t('templateCard.tags')}>
          {tags.map(tag => (
            <li key={tag} className={classes.tag}>
              {tag}
            </li>
          ))}
        </ul>
      )}
      {links.length > 0 && (
        <div className={classes.links}>
          {links.map(link => (
            <Link key={link.to} to={link.to}>
              {link.text}
            </Link>
          ))}
        </div>
      )}
      <div className={classes.footer}>
        <span className={classes.owner}>
          {template.spec.owner && (
            <EntityDisplayLink entityRef={template.spec.owner} />
          )}
        </span>
        {steps.length > 0 && (
          <button
            type="button"
            className={classes.stepsButton}
            aria-expanded={expanded}
            onClick={() => setExpanded(v => !v)}
          >
            {expanded
              ? t('templateCard.hideSteps')
              : t('templateCard.showSteps')}
          </button>
        )}
        <button
          type="button"
          className={classes.useButton}
          onClick={() => onSelected?.()}
        >
          {t('templateCard.use')}
        </button>
      </div>
      {expanded && (
        <ol className={classes.steps} aria-label={t('templateCard.steps')}>
          {steps.map((label, i) => (
            <li key={`${i}-${label}`} className={classes.step}>
              <span className={classes.stepNumber}>
                {String(i + 1).padStart(2, '0')}
              </span>
              <span>{label}</span>
            </li>
          ))}
        </ol>
      )}
    </article>
  );
}
